import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Button,
  Alert,
  CircularProgress,
  IconButton,
  InputAdornment,
  List,
  ListItem,
  ListItemIcon,
  ListItemText,
  Box,
} from '@mui/material';
import {
  Visibility,
  VisibilityOff,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
} from '@mui/icons-material';
import { authService } from '../../services/api';

const ChangePasswordModal = ({ open, onClose, onSuccess }) => {
  const { t } = useTranslation();
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword1, setNewPassword1] = useState('');
  const [newPassword2, setNewPassword2] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword1, setShowNewPassword1] = useState(false);
  const [showNewPassword2, setShowNewPassword2] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [backendErrors, setBackendErrors] = useState([]);

  // Validações de senha
  const passwordValidation = {
    minLength: newPassword1.length >= 9,
    hasUpperCase: /[A-Z]/.test(newPassword1),
    hasLowerCase: /[a-z]/.test(newPassword1),
    hasNumber: /[0-9]/.test(newPassword1),
    hasSpecialChar: /[!@#$%^&*(),.?":{}|<>]/.test(newPassword1),
  };

  const isPasswordValid = Object.values(passwordValidation).every(Boolean);

  const ValidationItem = ({ valid, text }) => (
    <ListItem dense sx={{ py: 0.5 }}>
      <ListItemIcon sx={{ minWidth: 36 }}>
        {valid ? (
          <CheckCircleIcon sx={{ color: 'success.main', fontSize: 20 }} />
        ) : (
          <CancelIcon sx={{ color: 'error.main', fontSize: 20 }} />
        )}
      </ListItemIcon>
      <ListItemText
        primary={text}
        primaryTypographyProps={{
          variant: 'body2',
          sx: {
            color: valid ? 'success.main' : 'text.secondary',
          },
        }}
      />
    </ListItem>
  );

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setBackendErrors([]);

    if (!currentPassword || !newPassword1 || !newPassword2) {
      setError(t('changePassword.fillAllFields'));
      return;
    }

    if (newPassword1 !== newPassword2) {
      setError(t('changePassword.passwordMismatch'));
      return;
    }

    if (!isPasswordValid) {
      setError(t('changePassword.passwordRequirements'));
      return;
    }

    setLoading(true);

    try {
      await authService.changePassword(currentPassword, newPassword1, newPassword2);
      onSuccess && onSuccess();
      handleClose();
    } catch (err) {
      console.error('Change password error:', err);

      // Check if backend returned validation errors
      if (err.response?.data) {
        const errors = [];

        // Handle new_password2 errors (most common)
        if (err.response.data.new_password2) {
          errors.push(...err.response.data.new_password2);
        }

        // Handle new_password1 errors
        if (err.response.data.new_password1) {
          errors.push(...err.response.data.new_password1);
        }

        // Handle old_password errors
        if (err.response.data.old_password) {
          errors.push(...err.response.data.old_password);
        }

        // Handle generic non_field_errors
        if (err.response.data.non_field_errors) {
          errors.push(...err.response.data.non_field_errors);
        }

        if (errors.length > 0) {
          setBackendErrors(errors);
        } else {
          setError(t('changePassword.error'));
        }
      } else {
        setError(t('changePassword.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setCurrentPassword('');
    setNewPassword1('');
    setNewPassword2('');
    setShowCurrentPassword(false);
    setShowNewPassword1(false);
    setShowNewPassword2(false);
    setError('');
    setBackendErrors([]);
    onClose();
  };

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="sm" fullWidth>
      <DialogTitle>{t('changePassword.title')}</DialogTitle>
      <DialogContent>
        <Box component="form" onSubmit={handleSubmit} sx={{ mt: 2 }}>
          {error && (
            <Alert severity="error" sx={{ mb: 2 }}>
              {error}
            </Alert>
          )}

          {backendErrors.length > 0 && (
            <Alert severity="error" sx={{ mb: 2 }}>
              <Box component="ul" sx={{ margin: 0, paddingLeft: 2 }}>
                {backendErrors.map((err, index) => (
                  <li key={index}>{err}</li>
                ))}
              </Box>
            </Alert>
          )}

          <TextField
            margin="normal"
            required
            fullWidth
            name="currentPassword"
            label={t('changePassword.currentPassword')}
            type={showCurrentPassword ? 'text' : 'password'}
            id="currentPassword"
            autoComplete="current-password"
            value={currentPassword}
            onChange={(e) => setCurrentPassword(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    disabled={loading}
                    disableRipple
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {showCurrentPassword ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword1"
            label={t('changePassword.newPassword')}
            type={showNewPassword1 ? 'text' : 'password'}
            id="newPassword1"
            autoComplete="new-password"
            value={newPassword1}
            onChange={(e) => setNewPassword1(e.target.value)}
            disabled={loading}
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword1(!showNewPassword1)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    disabled={loading}
                    disableRipple
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {showNewPassword1 ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          <TextField
            margin="normal"
            required
            fullWidth
            name="newPassword2"
            label={t('changePassword.confirmPassword')}
            type={showNewPassword2 ? 'text' : 'password'}
            id="newPassword2"
            autoComplete="new-password"
            value={newPassword2}
            onChange={(e) => setNewPassword2(e.target.value)}
            disabled={loading}
            error={newPassword2.length > 0 && newPassword1 !== newPassword2}
            helperText={
              newPassword2.length > 0 && newPassword1 !== newPassword2
                ? t('changePassword.passwordMismatch')
                : ''
            }
            InputProps={{
              endAdornment: (
                <InputAdornment position="end">
                  <IconButton
                    onClick={() => setShowNewPassword2(!showNewPassword2)}
                    onMouseDown={(e) => e.preventDefault()}
                    edge="end"
                    disabled={loading}
                    disableRipple
                    sx={{
                      '&:hover': {
                        backgroundColor: 'transparent',
                      },
                    }}
                  >
                    {showNewPassword2 ? <VisibilityOff /> : <Visibility />}
                  </IconButton>
                </InputAdornment>
              ),
            }}
            sx={{ mb: 2 }}
          />

          {/* Password Requirements */}
          {newPassword1.length > 0 && (
            <Box sx={{ mb: 2 }}>
              <List sx={{ py: 0 }}>
                <ValidationItem
                  valid={passwordValidation.minLength}
                  text={t('changePassword.minLength')}
                />
                <ValidationItem
                  valid={passwordValidation.hasUpperCase}
                  text={t('changePassword.hasUpperCase')}
                />
                <ValidationItem
                  valid={passwordValidation.hasLowerCase}
                  text={t('changePassword.hasLowerCase')}
                />
                <ValidationItem
                  valid={passwordValidation.hasNumber}
                  text={t('changePassword.hasNumber')}
                />
                <ValidationItem
                  valid={passwordValidation.hasSpecialChar}
                  text={t('changePassword.hasSpecialChar')}
                />
              </List>
            </Box>
          )}
        </Box>
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2 }}>
        <Button onClick={handleClose} disabled={loading}>
          {t('changePassword.cancel')}
        </Button>
        <Button
          onClick={handleSubmit}
          variant="contained"
          disabled={loading}
          sx={{ minWidth: 100 }}
        >
          {loading ? (
            <CircularProgress size={24} color="inherit" />
          ) : (
            t('changePassword.save')
          )}
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default ChangePasswordModal;
